import React, { useCallback, useState } from 'react';

import { MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import useUserHook from 'hooks/useUserHook';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import classes from 'components/common/ProgramLibraryDropdownMenu/dropdownMenu.module.scss';

import { BulkAssignTags } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssignTags/bulkAssignTags';
import { BulkAssignUsers } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssingUsers/bulkAssignUsers';
import { BulkDeleteAction } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkDeleteAction/bulkDeleteAction';
import { BulkRollbackAction } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkRollbackAction/bulkRollbackAction';
import { BulkUploadActionsFile } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkUploadActionsFile/bulkUploadActionsFile';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedDropdownMenu, CustomizedTooltip } from 'components/common';

interface Props {
    tagsOptions: any[];
    searchedFilterData: any;
    bulkAssigneeToUserUniqIdNotification: number;
    bulkAssigneeToTagsUniqIdNotification: number;
    bulkRemoveTagsUniqIdNotification: number;
    bulkDeleteCardsUniqIdNotification: number;
    bulkRollbackUniqIdNotification: number;
    setIsBulkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    cardsCounters: any;
    versionDbId: number;
    pills: unknown[];
}

type ModalType =
    | 'BULK_ASSIGN_USERS'
    | 'BULK_ASSIGN_TAGS'
    | 'BULK_UPLOAD_ACTIONS_FILE'
    | 'BULK_DELETE_ACTION'
    | 'BULK_ROLLBACK_ACTION';

export const BulkAssign = ({
    tagsOptions,
    searchedFilterData,
    bulkAssigneeToUserUniqIdNotification,
    bulkAssigneeToTagsUniqIdNotification,
    bulkRemoveTagsUniqIdNotification,
    bulkDeleteCardsUniqIdNotification,
    bulkRollbackUniqIdNotification,
    setIsBulkLoading,
    cardsCounters,
    versionDbId,
    pills,
}: Props) => {
    const { ability } = useUserHook();
    const isBulkDeleteActionPrivilege = ability.can('view', 'ManageProjects');
    const [modalType, setModalType] = useState<ModalType | null>(null);
    const handleModalClose = useCallback(() => {
        setModalType(null);
    }, []);

    return (
        <>
            <CustomizedDropdownMenu
                triggerElement={
                    <CustomizedTooltip
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={`Bulk Actions`}
                        triggerElement={
                            <Button
                                data-testid={'bulk-actions'}
                                endIcon={
                                    <Icon
                                        name={'header-arrow-down'}
                                        size={'1.5rem'}
                                        color={'var(--button-secondary-fg)'}
                                    />
                                }
                                startIcon={
                                    <MuiIcon
                                        icon={'dynamic_feed'}
                                        fontSize={'1.5rem'}
                                        color={'var(--button-secondary-fg)'}
                                    />
                                }
                                variant={'secondary'}
                            >
                                Bulk Actions
                            </Button>
                        }
                    />
                }
            >
                <MenuItem
                    onClick={() => {
                        setModalType('BULK_ASSIGN_USERS');
                    }}
                >
                    Bulk Assign Users
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setModalType('BULK_ASSIGN_TAGS');
                    }}
                >
                    Bulk Edit Tags
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setModalType('BULK_UPLOAD_ACTIONS_FILE');
                    }}
                >
                    Bulk Upload User Actions
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setModalType('BULK_ROLLBACK_ACTION');
                    }}
                >
                    Bulk Rollback Status to Submitted (Original)
                </MenuItem>
                {isBulkDeleteActionPrivilege && (
                    <MenuItem
                        onClick={() => {
                            setModalType('BULK_DELETE_ACTION');
                        }}
                    >
                        Bulk Delete User Actions & Recommendations
                    </MenuItem>
                )}
                <MenuItem>
                    <Link
                        className={classes.programLibraryMenuLink}
                        to={AppRoutes.buildPath(
                            AppRoutes.DASHBOARD_EXECUTION_PATH,
                            executionConfig.battlecards.link,
                            executionConfig.editBulkActionRules.link,
                        )}
                    >
                        Edit Bulk Action Rules
                    </Link>
                </MenuItem>
            </CustomizedDropdownMenu>

            <BulkAssignUsers
                searchedFilterData={searchedFilterData}
                bulkAssigneeToUserUniqIdNotification={bulkAssigneeToUserUniqIdNotification}
                setIsBulkLoading={setIsBulkLoading}
                cardsCounters={cardsCounters}
                handleModalClose={handleModalClose}
                isShowModal={modalType === 'BULK_ASSIGN_USERS'}
                pills={pills}
            />

            <BulkAssignTags
                tagsOptions={tagsOptions}
                searchedFilterData={searchedFilterData}
                bulkAssigneeToTagsUniqIdNotification={bulkAssigneeToTagsUniqIdNotification}
                bulkRemoveTagsUniqIdNotification={bulkRemoveTagsUniqIdNotification}
                setIsBulkLoading={setIsBulkLoading}
                cardsCounters={cardsCounters}
                handleModalClose={handleModalClose}
                isShowModal={modalType === 'BULK_ASSIGN_TAGS'}
                pills={pills}
            />

            <BulkUploadActionsFile
                tagsOptions={tagsOptions}
                versionDbId={versionDbId}
                handleModalClose={handleModalClose}
                isShowModal={modalType === 'BULK_UPLOAD_ACTIONS_FILE'}
            />

            <BulkDeleteAction
                cardsCounters={cardsCounters}
                setIsBulkLoading={setIsBulkLoading}
                searchedFilterData={searchedFilterData}
                bulkDeleteCardsUniqIdNotification={bulkDeleteCardsUniqIdNotification}
                handleModalClose={handleModalClose}
                isShowModal={modalType === 'BULK_DELETE_ACTION'}
            />

            <BulkRollbackAction
                cardsCounters={cardsCounters}
                setIsBulkLoading={setIsBulkLoading}
                searchedFilterData={searchedFilterData}
                bulkRollbackUniqIdNotification={bulkRollbackUniqIdNotification}
                handleModalClose={handleModalClose}
                isShowModal={modalType === 'BULK_ROLLBACK_ACTION'}
            />
        </>
    );
};
